import type { EnvelopeId } from './envelope'
import type { Xof } from './money'
import type { PeriodId } from './period'

/**
 * Journal append-only. Une opération (identifiée par un UUID généré côté client) produit une
 * ou plusieurs lignes. Le type de ligne fixe le sens comptable : aucun montant signé.
 * Une écriture n'est jamais modifiée ni supprimée : on l'annule par une opération `reversal`.
 */

export type OperationId = string
export type OccurrenceId = string

interface EntryBase {
  operationId: OperationId
  /** Rang de la ligne dans l'opération, à partir de 0. */
  line: number
  periodId: PeriodId
  /** Instant de l'opération (ISO 8601), choisi par l'utilisateur. */
  occurredAt: string
}

/** Revenu effectivement reçu. Un revenu attendu n'entre pas dans le journal. */
export interface IncomeEntry extends EntryBase {
  kind: 'income'
  amount: Xof
  title: string
  occurrenceId: OccurrenceId | null
}

/** Affectation d'argent non affecté à une enveloppe. Ni revenu, ni dépense. */
export interface AllocationEntry extends EntryBase {
  kind: 'allocation'
  envelopeId: EnvelopeId
  amount: Xof
}

export interface ExpenseEntry extends EntryBase {
  kind: 'expense'
  envelopeId: EnvelopeId
  amount: Xof
  title: string
  occurrenceId: OccurrenceId | null
}

export type TransferOrigin = 'manual' | 'shortfall'

export interface TransferEntry extends EntryBase {
  kind: 'transfer'
  sourceEnvelopeId: EnvelopeId
  destinationEnvelopeId: EnvelopeId
  amount: Xof
  origin: TransferOrigin
  note: string | null
}

/** Montant mis de côté pour une échéance future. Ce n'est pas une dépense. */
export interface ReservationEntry extends EntryBase {
  kind: 'reservation'
  envelopeId: EnvelopeId
  amount: Xof
  occurrenceId: OccurrenceId
}

/** Libération d'une réservation (échéance payée, ignorée ou modifiée). */
export interface ReleaseEntry extends EntryBase {
  kind: 'release'
  envelopeId: EnvelopeId
  amount: Xof
  occurrenceId: OccurrenceId
}

/**
 * Correction d'un solde, avec motif obligatoire (solde d'ouverture, écart constaté…).
 * `envelopeId = null` vise l'argent non affecté.
 */
export interface AdjustmentEntry extends EntryBase {
  kind: 'adjustment'
  envelopeId: EnvelopeId | null
  direction: 'credit' | 'debit'
  amount: Xof
  reason: string
}

/** Annule l'intégralité d'une opération antérieure. */
export interface ReversalEntry extends EntryBase {
  kind: 'reversal'
  reversedOperationId: OperationId
  reason: string
}

export type LedgerEntry =
  | IncomeEntry
  | AllocationEntry
  | ExpenseEntry
  | TransferEntry
  | ReservationEntry
  | ReleaseEntry
  | AdjustmentEntry
  | ReversalEntry

export type LedgerEntryKind = LedgerEntry['kind']

/** Opérations annulées par une opération `reversal` présente dans la liste. */
export function reversedOperations(entries: readonly LedgerEntry[]): Set<OperationId> {
  const reversed = new Set<OperationId>()
  for (const entry of entries) {
    if (entry.kind === 'reversal') reversed.add(entry.reversedOperationId)
  }
  return reversed
}

/** Lignes qui ont un effet comptable : ni annulées, ni annulations elles-mêmes. */
export function effectiveEntries(entries: readonly LedgerEntry[]): LedgerEntry[] {
  const reversed = reversedOperations(entries)
  return entries.filter((entry) => entry.kind !== 'reversal' && !reversed.has(entry.operationId))
}
